import { Plus, X } from 'lucide-react'
import { useEquipmentScope } from './equipmentScope'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'

/** Local row shape for the checklist editor (needs a stable key while editing). */
export type ChecklistRow = { key: string; label: string; required: boolean }

let checklistRowSeq = 0
export function newChecklistRow(label: string, required: boolean): ChecklistRow {
  checklistRowSeq += 1
  return { key: `row-${checklistRowSeq}`, label, required }
}

/** Sensible starting template — technicians can edit or remove any of these. */
export function defaultChecklistRows(): ChecklistRow[] {
  return [
    newChecklistRow('LOTO isolated & verified', true),
    newChecklistRow('Reference standards in-calibration', true),
    newChecklistRow('Line clearance verified (QA)', false),
  ]
}

/** Editable list of pre-start checklist items — add/edit label/toggle required/remove. */
export function ChecklistEditor({
  rows,
  onChange,
}: {
  rows: ChecklistRow[]
  onChange: (rows: ChecklistRow[]) => void
}) {
  const { copy } = useEquipmentScope()
  const updateRow = (key: string, patch: Partial<ChecklistRow>) => {
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }
  const removeRow = (key: string) => {
    onChange(rows.filter((r) => r.key !== key))
  }
  const addRow = () => {
    onChange([...rows, newChecklistRow('', false)])
  }

  return (
    <div className='space-y-1.5'>
      <label className='text-xs font-medium text-gray-600'>Pre-start checklist</label>
      <p className='text-[11px] text-gray-400'>
        {copy.checklistHint}
      </p>
      <div className='space-y-2'>
        {rows.map((row) => (
          <div key={row.key} className='flex items-center gap-2'>
            <Input
              value={row.label}
              onChange={(e) => updateRow(row.key, { label: e.target.value })}
              placeholder='Checklist item'
              className='flex-1'
            />
            <label className='flex shrink-0 items-center gap-1.5 text-xs text-gray-600'>
              <Checkbox
                checked={row.required}
                onCheckedChange={(c) => updateRow(row.key, { required: c === true })}
              />
              Required
            </label>
            <button
              type='button'
              onClick={() => removeRow(row.key)}
              className='shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-rose-500'
              title='Remove item'
            >
              <X className='h-3.5 w-3.5' />
            </button>
          </div>
        ))}
      </div>
      <button
        type='button'
        onClick={addRow}
        className='inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/5'
      >
        <Plus className='h-3.5 w-3.5' />
        Add item
      </button>
    </div>
  )
}
